// Calculation engine entry point — PRODUCT-53 BOM generator.
// Orchestrates compute, storage, and network engines then assembles the BOM audit trail.

import type {
  Project,
  VMConfig,
  SizingConfig,
  SizingResult,
  BOMLineItem,
  ComputeResult,
  StorageResult,
  NetworkResult,
  LoadBalancingType,
} from '../../types';
import { runComputeSizing } from './compute';
import { runStorageSizing } from './storage';
import { runNetworkSizing } from './network';

export { runComputeSizing } from './compute';
export { runStorageSizing } from './storage';
export { runNetworkSizing } from './network';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractAllVMs(project: Project): VMConfig[] {
  return project.applications.flatMap(app => app.vms);
}

function totalHostCountAtSite(computeResults: ComputeResult[], site: 'primary' | 'dr'): number {
  return computeResults
    .filter(r => r.site === site)
    .reduce((sum, r) => sum + r.hostCountWithHA, 0);
}

// ─── BOM Assemblers ───────────────────────────────────────────────────────────

function buildComputeBOMLines(computeResults: ComputeResult[]): BOMLineItem[] {
  const lines: BOMLineItem[] = [];

  for (const result of computeResults) {
    if (result.hostCountWithHA === 0) {
      // Cold DR — emit an explanatory line rather than silently omitting
      lines.push({
        category: 'Compute',
        site: result.site === 'primary' ? 'Primary' : 'DR',
        environment: result.environment,
        description: 'No compute provisioned — Cold DR tier',
        quantity: 0,
        unit: 'hosts',
        specNotes: 'Cold DR: compute is not provisioned at DR site. Storage replication continues independently.',
        methodologyNote: `[${result.tierName ?? 'All'}] ${result.notes.join(' | ')}`,
        unitCost: null,
        currency: null,
        tierId: result.tierId,
        tierName: result.tierName,
      });
      continue;
    }

    const spec = result.recommendedHostSpec;
    const specNote = [
      `≥${spec.physicalCores} physical cores`,
      `≥${spec.memoryGb} GB RAM`,
      `≥${spec.minCpuGhz} GHz`,
    ].join(', ');

    const haWarning = result.hasUltraLowLatencyVms
      ? ` WARNING: ${result.latencyConstraintCount} VM type(s) require ultra-low latency — review co-location policy.`
      : '';

    const tierPrefix = result.tierName ? `${result.tierName} — ` : '';

    lines.push({
      category: 'Compute',
      site: result.site === 'primary' ? 'Primary' : 'DR',
      environment: result.environment,
      description: `${tierPrefix}${result.hostCountWithHA} × virtualisation host (${result.hostCountWithoutHA} + HA spares)`,
      quantity: result.hostCountWithHA,
      unit: 'hosts',
      specNotes: `Per-host spec: ${specNote}.${haWarning}`,
      methodologyNote: `[${result.tierName ?? 'All'}] ${result.notes.join(' | ')}`,
      unitCost: null,
      currency: null,
      tierId: result.tierId,
      tierName: result.tierName,
    });
  }

  return lines;
}

function buildStorageBOMLines(storageResults: StorageResult[]): BOMLineItem[] {
  const lines: BOMLineItem[] = [];

  for (const result of storageResults) {
    const siteName: 'Primary' | 'DR' | 'Both' = result.site === 'primary' ? 'Primary' : 'DR';
    const methodologyBase = result.notes.join(' | ');

    if (result.rawSanGb > 0) {
      lines.push({
        category: 'Storage',
        site: siteName,
        environment: 'all',
        description: 'SAN raw capacity',
        quantity: Math.ceil(result.rawSanGb),
        unit: 'GB',
        specNotes: `Usable: ${result.usableSanGb} GB → after growth buffer and RAID overhead: ${Math.ceil(result.rawSanGb)} GB raw`,
        methodologyNote: methodologyBase,
        unitCost: null,
        currency: null,
      });
    }

    if (result.rawNasGb > 0) {
      lines.push({
        category: 'Storage',
        site: siteName,
        environment: 'all',
        description: 'NAS raw capacity',
        quantity: Math.ceil(result.rawNasGb),
        unit: 'GB',
        specNotes: `Usable: ${result.usableNasGb} GB → after growth buffer and RAID overhead: ${Math.ceil(result.rawNasGb)} GB raw`,
        methodologyNote: methodologyBase,
        unitCost: null,
        currency: null,
      });
    }

    if (result.rawLocalGb > 0) {
      lines.push({
        category: 'Storage',
        site: siteName,
        environment: 'all',
        description: 'Local (direct-attach) raw capacity',
        quantity: Math.ceil(result.rawLocalGb),
        unit: 'GB',
        specNotes: `Usable: ${result.usableLocalGb} GB → after growth buffer and RAID overhead: ${Math.ceil(result.rawLocalGb)} GB raw`,
        methodologyNote: methodologyBase,
        unitCost: null,
        currency: null,
      });
    }

    if (result.effectiveIopsRequired > 0) {
      lines.push({
        category: 'Storage',
        site: siteName,
        environment: 'all',
        description: 'SAN IOPS requirement',
        quantity: Math.ceil(result.effectiveIopsRequired),
        unit: 'IOPS',
        specNotes: `Total nominal IOPS: ${result.totalSanIops}. Effective after RAID write penalty: ${Math.ceil(result.effectiveIopsRequired)} IOPS`,
        methodologyNote: methodologyBase,
        unitCost: null,
        currency: null,
      });
    }
  }

  return lines;
}

function buildNetworkBOMLines(networkResults: NetworkResult[]): BOMLineItem[] {
  const lines: BOMLineItem[] = [];

  for (const result of networkResults) {
    const siteName: 'Primary' | 'DR' | 'Both' = result.site === 'primary' ? 'Primary' : 'DR';
    const methodologyBase = result.notes.join(' | ');

    lines.push({
      category: 'Network',
      site: siteName,
      environment: 'all',
      description: `North-south uplink — ${result.recommendedUplinkGbps} Gbps`,
      quantity: result.recommendedUplinkGbps,
      unit: 'Gbps',
      specNotes: `North-south bandwidth: ${result.northSouthBandwidthMbps} Mbps rounded up to ${result.recommendedUplinkGbps} Gbps standard tier. East-west (incl. vMotion 20% overhead): ${result.eastWestBandwidthMbps} Mbps.`,
      methodologyNote: methodologyBase,
      unitCost: null,
      currency: null,
    });

    lines.push({
      category: 'Network',
      site: siteName,
      environment: 'all',
      description: 'Access switch ports',
      quantity: result.recommendedAccessPortCount,
      unit: 'ports',
      specNotes: `${result.recommendedAccessPortCount} ports = (hosts × 2 dual-homed NIC ports) + (hosts × 2 storage ports)`,
      methodologyNote: methodologyBase,
      unitCost: null,
      currency: null,
    });
  }

  return lines;
}

function buildSecurityBOMLines(
  networkResults: NetworkResult[],
  vms: VMConfig[],
): BOMLineItem[] {
  // VLAN count is project-wide — derived from distinct security domains across all VMs
  const distinctDomains = new Set(vms.map(vm => vm.networkConfig.securityDomain));
  const vlanCount = distinctDomains.size;

  if (vlanCount === 0) return [];

  // Use the primary site network result if available, else DR
  const primaryResult = networkResults.find(r => r.site === 'primary');
  const representativeResult = primaryResult ?? networkResults[0];

  if (!representativeResult) return [];

  const domainList = [...distinctDomains].sort().join(', ');

  return [
    {
      category: 'Security',
      site: 'Both',
      environment: 'all',
      description: `${vlanCount} VLAN segment(s) — security zone separation`,
      quantity: vlanCount,
      unit: 'VLANs',
      specNotes: `Security domains in scope: ${domainList}. One VLAN per domain minimum; additional VLANs per environment may be required.`,
      methodologyNote:
        `Required VLAN count derived from distinct VM security domains: [${domainList}]. ` +
        `Count = ${vlanCount}. Source: network engine requiredVlanCount. ` +
        `Each distinct securityDomain value maps to at least one VLAN segment.`,
      unitCost: null,
      currency: null,
    },
  ];
}

function buildLoadBalancingBOMLines(vms: VMConfig[]): BOMLineItem[] {
  // Group VMs by load balancing type (exclude 'none')
  const lbTypes = new Map<
    LoadBalancingType,
    { vms: VMConfig[]; totalSessionsPerSec: number }
  >();

  for (const vm of vms) {
    const lb = vm.networkConfig.loadBalancing;
    if (lb === 'none') continue;

    const existing = lbTypes.get(lb);
    const sessionsContribution =
      (vm.networkConfig.loadBalancingSessionsPerSec ?? 0) * vm.quantity;

    if (existing) {
      existing.vms.push(vm);
      existing.totalSessionsPerSec += sessionsContribution;
    } else {
      lbTypes.set(lb, { vms: [vm], totalSessionsPerSec: sessionsContribution });
    }
  }

  const lines: BOMLineItem[] = [];

  for (const [lbType, { vms: lbVms, totalSessionsPerSec }] of lbTypes.entries()) {
    const vmNames = lbVms.map(vm => vm.name).join(', ');
    const isExternal = lbType.startsWith('external');
    const tier = lbType.includes('l7') ? 'L7 (application-aware)' : 'L4 (transport)';
    const scope = isExternal ? 'External' : 'Internal';

    const specNotes = [
      `${scope} load balancer — ${tier}`,
      `Applicable VMs: ${vmNames}`,
      totalSessionsPerSec > 0
        ? `Combined sessions/sec: ${totalSessionsPerSec.toLocaleString()}`
        : 'Sessions/sec not specified',
    ].join('. ');

    lines.push({
      category: 'Load Balancing',
      site: 'Both',
      environment: 'all',
      description: `${scope} load balancer — ${tier}`,
      quantity: 1,
      unit: 'instance',
      specNotes,
      methodologyNote:
        `Load balancing requirement derived from VM networkConfig.loadBalancing = '${lbType}'. ` +
        `${lbVms.length} VM type(s) require this LB type. ` +
        `${totalSessionsPerSec > 0 ? `Aggregate sessions/sec: ${totalSessionsPerSec}.` : 'No sessions/sec specified — size by traffic volume.'} ` +
        `LB lines are project-wide (site = Both); dedicated per-site instances may be required for Active-Active DR.`,
      unitCost: null,
      currency: null,
    });
  }

  return lines;
}

// ─── Main Entry Point ─────────────────────────────────────────────────────────

/**
 * Run the full sizing pipeline for a given project and scenario.
 * Calls compute, storage, and network engines then assembles the BOM audit trail.
 *
 * @param project - Full project including all applications and scenarios
 * @param scenarioId - ID of the scenario whose SizingConfig to use
 * @returns SizingResult with compute[], storage[], network[], and bom[]
 */
export function runSizing(project: Project, scenarioId: string): SizingResult {
  const scenario = project.scenarios.find(s => s.id === scenarioId);
  if (!scenario) {
    throw new Error(`Scenario '${scenarioId}' not found in project '${project.id}'`);
  }

  const config: SizingConfig = scenario.sizingConfig;
  const allVMs: VMConfig[] = extractAllVMs(project);

  // ── Compute engine ────────────────────────────────────────────────────────
  const primaryCompute = runComputeSizing(allVMs, config, 'primary', project.applications);
  const drCompute = runComputeSizing(allVMs, config, 'dr', project.applications);
  const computeResults: ComputeResult[] = [...primaryCompute, ...drCompute];

  // ── Storage engine ────────────────────────────────────────────────────────
  const primaryStorage = runStorageSizing(allVMs, config, 'primary');
  const drStorage = runStorageSizing(allVMs, config, 'dr');
  const storageResults: StorageResult[] = [primaryStorage, drStorage];

  // ── Network engine — needs HA-adjusted host count from compute ────────────
  const primaryHostCount = totalHostCountAtSite(computeResults, 'primary');
  const drHostCount = totalHostCountAtSite(computeResults, 'dr');

  const primaryNetwork = runNetworkSizing(allVMs, config, 'primary', primaryHostCount);
  const drNetwork = runNetworkSizing(allVMs, config, 'dr', drHostCount);
  const networkResults: NetworkResult[] = [primaryNetwork, drNetwork];

  // ── BOM assembly ──────────────────────────────────────────────────────────
  const bom: BOMLineItem[] = [
    ...buildComputeBOMLines(computeResults),
    ...buildStorageBOMLines(storageResults),
    ...buildNetworkBOMLines(networkResults),
    ...buildSecurityBOMLines(networkResults, allVMs),
    ...buildLoadBalancingBOMLines(allVMs),
  ];

  return {
    projectId: project.id,
    scenarioId,
    generatedAt: new Date().toISOString(),
    compute: computeResults,
    storage: storageResults,
    network: networkResults,
    bom,
  };
}
